import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  X,
  Trophy,
  Calendar,
  Clock,
  Users,
  MapPin,
  FileText,
  Target,
  Activity,
  Paperclip
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface Participant {
  user: { _id: string; name: string; avatar?: string } | string;
  status: string;
  registeredAt: string;
}

interface Tournament {
  _id: string;
  title: string;
  sport: string;
  status: string;
  location: string;
  date: string;
  time: string;
  maxParticipants: number;
  participantCount?: number;
  bannerImage?: string;
  description: string;
  rules?: string[];
  organizer?: string;
  registrationDeadline: string;
  prizePool?: string;
  participants?: Participant[];
}

const TournamentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'participants' | 'brackets' | 'attachments' | 'moderation'>('overview');
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);

  const loadTournament = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/tournaments/${id}`);
      if (res.data.success) setTournament(res.data.data);
    } catch (err) {
      console.error('Failed to load tournament:', err);
      toast.error('Failed to load tournament');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournament();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'ongoing': return 'bg-green-100 text-green-800';
      case 'postponed': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB');
  };

  const handleEditTournament = () => {
    navigate('/admin/tournaments');
  };

  const handleCancelTournament = async () => {
    if (!tournament || !id) return;
    if (!window.confirm(`Cancel ${tournament.title}? This action cannot be undone.`)) return;

    try {
      await api.put(`/tournaments/${id}`, { status: 'cancelled' });
      toast.success('Tournament cancelled');
      loadTournament();
    } catch (err) {
      console.error('Failed to cancel tournament:', err);
      toast.error('Failed to cancel tournament');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
        <p className="text-gray-500 mt-2">Loading tournament...</p>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="text-center py-12 text-gray-500">
        Tournament not found.
        <button
          onClick={() => navigate('/admin/tournaments')}
          className="block mx-auto mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Tournaments
        </button>
      </div>
    );
  }

  const participants = tournament.participants || [];

  return (
    <div className="space-y-6">
      {/* Back Navigation */}
      <button
        onClick={() => navigate('/admin/tournaments')}
        className="inline-flex items-center text-blue-600 hover:text-blue-800"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Tournaments
      </button>

      {/* Banner Image */}
      <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
        {tournament.bannerImage ? (
          <img src={tournament.bannerImage} alt="Tournament banner" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Trophy className="w-16 h-16" />
          </div>
        )}
      </div>

      {/* Tournament Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          {/* Tournament Info */}
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
              <Trophy className="w-8 h-8 text-gray-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                <h1 className="text-2xl font-bold text-gray-900">{tournament.title}</h1>
                <span className="px-2 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                  #{tournament.sport}
                </span>
                <span className={`px-2 py-1 text-sm font-medium rounded-full ${getStatusBadgeColor(tournament.status)}`}>
                  {tournament.status.charAt(0).toUpperCase() + tournament.status.slice(1)}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>{tournament.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(tournament.date)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>{tournament.time}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>{tournament.participantCount ?? participants.length}/{tournament.maxParticipants} participants</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleEditTournament}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              onClick={handleCancelTournament}
              disabled={tournament.status === 'cancelled'}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'overview', label: 'Overview', icon: FileText },
            { key: 'participants', label: 'Participants', icon: Users },
            { key: 'brackets', label: 'Brackets/Results', icon: Target },
            { key: 'attachments', label: 'Attachments', icon: Paperclip },
            { key: 'moderation', label: 'Moderation', icon: Activity }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {activeTab === 'overview' && (
          <>
            {/* Left Column - Tournament Information */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tournament Information</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{tournament.description}</p>

                {tournament.rules && tournament.rules.length > 0 && (
                  <>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Tournament Rules</h4>
                    <ul className="space-y-2">
                      {tournament.rules.map((rule, index) => (
                        <li key={index} className="flex items-start space-x-2 text-gray-600">
                          <span className="text-gray-400 mt-1">•</span>
                          <span>{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>

            {/* Right Column - Details */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Organizer</label>
                    <p className="text-sm text-gray-900">{tournament.organizer || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Registration Deadline</label>
                    <p className="text-sm text-gray-900">{formatDateTime(tournament.registrationDeadline)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Max Participants</label>
                    <p className="text-sm text-gray-900">{tournament.maxParticipants}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Prize Pool</label>
                    <p className="text-sm text-gray-900 font-semibold text-green-600">{tournament.prizePool || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'participants' && (
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {participants.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {participants.map((p, index) => {
                    const user = typeof p.user === 'object' ? p.user : null;
                    return (
                      <li key={user?._id || index} className="py-3 flex items-center justify-between">
                        <span className="text-sm text-gray-900">{user?.name || 'Unknown user'}</span>
                        <span className="text-xs text-gray-500 capitalize">{p.status}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No participants yet</h3>
                  <p className="text-gray-500">Participants will appear here when they register for the tournament.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'brackets' && (
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-center py-12">
                <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No brackets created</h3>
                <p className="text-gray-500">Tournament brackets and results will be displayed here.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'attachments' && (
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-center py-12">
                <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No attachments</h3>
                <p className="text-gray-500">Tournament documents and attachments will appear here.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'moderation' && (
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No moderation logs</h3>
                <p className="text-gray-500">Tournament moderation activities will be logged here.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentDetail;
